import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useClinic } from "../../../../components/Context/ClinicaContext";
import { TabelaProcedimentoService } from "../../../../services/ADM/TabelaProcedimentoService";

export const useTabelasProcedimentos = (tipo) => {
  const { selectedClinic } = useClinic();
  return useQuery({
    queryKey: ["tabelasProcedimentos", selectedClinic?._id, tipo],
    queryFn: () => TabelaProcedimentoService.listarTabelas(selectedClinic._id, tipo),
    enabled: !!selectedClinic?._id,
    refetchOnWindowFocus: false,
  });
};

export const useTabelaProcedimento = (id) =>
  useQuery({
    queryKey: ["tabelaProcedimento", id],
    queryFn: () => TabelaProcedimentoService.buscarTabelaPorId(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

export const useItensTabela = (tabelaId, { page = 1, limit = 20, q = "" } = {}) =>
  useQuery({
    queryKey: ["itensTabela", tabelaId, page, limit, q],
    queryFn: () => TabelaProcedimentoService.listarItens(tabelaId, { page, limit, q }),
    enabled: !!tabelaId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

export const useImportarTabelaProcedimento = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { selectedClinic } = useClinic();

  return useMutation({
    mutationFn: (data) => TabelaProcedimentoService.importar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tabelasProcedimentos", selectedClinic?._id] });
      queryClient.invalidateQueries({ queryKey: ["itensTabela"] });
      toast({
        title: "Sucesso",
        description: "Tabela de procedimentos importada com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: error.message || "Falha ao importar tabela de procedimentos.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });
};

export const useCriarTabelaProcedimento = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { selectedClinic } = useClinic();

  return useMutation({
    mutationFn: (data) => TabelaProcedimentoService.criar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tabelasProcedimentos", selectedClinic?._id] });
      toast({
        title: "Sucesso",
        description: "Tabela criada com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: error.message || "Falha ao criar tabela.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });
};

export const useAtualizarTabelaProcedimento = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { selectedClinic } = useClinic();

  return useMutation({
    mutationFn: ({ id, data }) => TabelaProcedimentoService.atualizar(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tabelasProcedimentos", selectedClinic?._id] });
      toast({
        title: "Sucesso",
        description: "Tabela atualizada com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: error.message || "Falha ao atualizar tabela.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });
};

export const useExcluirTabelaProcedimento = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { selectedClinic } = useClinic();

  return useMutation({
    mutationFn: (id) => TabelaProcedimentoService.remover(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tabelasProcedimentos", selectedClinic?._id] });
      toast({
        title: "Sucesso",
        description: "Tabela excluída com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: error.message || "Falha ao excluir tabela.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });
};

export const useCriarItemTabela = (tabelaId) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data) => TabelaProcedimentoService.criarItem(tabelaId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itensTabela", tabelaId] });
      queryClient.invalidateQueries({ queryKey: ["tabelasProcedimentos"] });
      toast({ title: "Sucesso", description: "Item criado com sucesso.", status: "success", duration: 4000, isClosable: true });
    },
    onError: (error) => {
      toast({ title: "Erro", description: error.message || "Falha ao criar item.", status: "error", duration: 5000, isClosable: true });
    },
  });
};

export const useAtualizarItemTabela = (tabelaId) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ itemId, data }) => TabelaProcedimentoService.atualizarItem(tabelaId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itensTabela", tabelaId] });
      toast({ title: "Sucesso", description: "Item atualizado com sucesso.", status: "success", duration: 4000, isClosable: true });
    },
    onError: (error) => {
      toast({ title: "Erro", description: error.message || "Falha ao atualizar item.", status: "error", duration: 5000, isClosable: true });
    },
  });
};

export const useExcluirItemTabela = (tabelaId) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (itemId) => TabelaProcedimentoService.removerItem(tabelaId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itensTabela", tabelaId] });
      queryClient.invalidateQueries({ queryKey: ["tabelasProcedimentos"] });
      toast({ title: "Sucesso", description: "Item excluído com sucesso.", status: "success", duration: 4000, isClosable: true });
    },
    onError: (error) => {
      toast({ title: "Erro", description: error.message || "Falha ao excluir item.", status: "error", duration: 5000, isClosable: true });
    },
  });
};
